import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { PageShell } from "@project/user/components/page-shell";
import {
  Reveal,
  Stagger,
  StaggerItem,
} from "@project/user/components/motion";
import { Progress } from "@project/components/ui/progress";
import { Button } from "@project/components/ui/button";
import { ManavSevaBankDetails } from "@project/user/components/manav-seva-bank-details";
import {
  getHubAggregateStats,
  schemeToHubTile,
  type DonationHubTile,
} from "@project/donate/lib/scheme-mappers";
import {
  publicDonationCategoryService,
  publicDonationSchemeService,
} from "@project/services/donate";
import { useI18n } from "@project/lib/use-i18n";
import {
  Heart,
  Target,
  ArrowRight,
  TrendingUp,
  Award,
  Flame,
  Shield,
  Package,
  Filter,
} from "lucide-react";

type Kind = DonationHubTile["kind"];
type Tile = DonationHubTile;

const inr = (n: number) => `₹${Number(n ?? 0).toLocaleString("en-IN")}`;

// Contextual fallback imagery (Unsplash) so every card shows a relevant photo
// even when the admin hasn't uploaded one yet.
const img = (id: string, w = 900) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=70`;

const FALLBACK_IMAGES: Record<Kind, string[]> = {
  campaign: [
    img("1488521787991-ed7bbaae773c"),
    img("1518568814500-bf0f8d125f46"),
    img("1469571486292-0ba58a3f068b"),
    img("1532629345422-7515f3d16bb6"),
  ],
};

// Granular keyword → image map. Order matters (first match wins).
const KEYWORD_IMAGES: { match: RegExp; url: string }[] = [
  {
    match: /tiffin|thali|bhojan|prasad/i,
    url: img("1567337710282-00832b415979"),
  },
  {
    match: /meal|food|lunch|dinner|breakfast|kitchen|annadan|anna\s/i,
    url: img("1546548970-71785318a17b"),
  },
  {
    match: /grain|rice|wheat|dal|atta|flour|ration/i,
    url: img("1586201375761-83865001e31c"),
  },
  { match: /milk|dairy/i, url: img("1550583724-b2692b85b150") },
  { match: /water|borewell|hydrat/i, url: img("1548839140-29a749e1cf4d") },
  { match: /hospital|surger|operat/i, url: img("1519494026892-80bbd2d6fd0d") },
  {
    match: /medic|health|medicine|doctor|pharmacy|treatment/i,
    url: img("1576091160550-2173dba999ef"),
  },
  {
    match: /wheelchair|walker|crutch|equipment|aid/i,
    url: img("1559757175-08d1ed4e10b3"),
  },
  { match: /ambulance|emergenc/i, url: img("1587351021355-a479a299d2f9") },
  {
    match: /eye|vision|spectacle|cataract/i,
    url: img("1582719471384-894fbb16e074"),
  },
  { match: /dental|teeth|tooth/i, url: img("1606811971618-4486d14f3f99") },
  {
    match: /room|bed|dormitor|ward|cottage/i,
    url: img("1586773860418-d37222d8fce3"),
  },
  {
    match: /building|construct|infra|hall|dining/i,
    url: img("1504439468489-c8920d796a29"),
  },
  {
    match: /solar|electric|power|generator/i,
    url: img("1509391366360-2e959784a276"),
  },
  {
    match: /cloth|saree|kurta|dhoti|garment/i,
    url: img("1489710437720-ebb67ec84dd2"),
  },
  {
    match: /blanket|winter|warm|sweater|shawl/i,
    url: img("1517502884422-41eaead166d4"),
  },
  {
    match: /shoe|footwear|chappal|sandal/i,
    url: img("1542291026-7eec264c27ff"),
  },
  {
    match: /educat|school|book|student|scholar|tuition/i,
    url: img("1503676260728-1c00da094a0b"),
  },
  {
    match: /stationery|notebook|pencil/i,
    url: img("1456513080510-7bf3a84b82f8"),
  },
  { match: /child|kid|orphan/i, url: img("1503454537195-1dcabb73ffb9") },
  { match: /birthday|anniversar/i, url: img("1464349095431-e9a21285b5f3") },
  {
    match: /punya|shradh|tithi|memori/i,
    url: img("1518609878373-06d740f60d8b"),
  },
  {
    match: /festival|diwali|pooja|puja|navrat|janmasht|holi|rakhi|onam|pongal/i,
    url: img("1543353071-10c8ba85a904"),
  },
  {
    match: /gau|goshala|cattle|\bcow\b/i,
    url: img("1500595046743-cd271d694d30"),
  },
  {
    match: /tree|plant|garden|environment|green/i,
    url: img("1542601906990-b4d3fb778b09"),
  },
  {
    match: /elder|senior|\bold\b|resident|grand|vridh|vriddh|aged/i,
    url: img("1488521787991-ed7bbaae773c"),
  },
  { match: /widow|women|mahila/i, url: img("1573496359142-b8d87734a5a2") },
  {
    match: /yoga|meditat|spiritual|satsang|bhajan|kirtan/i,
    url: img("1545389336-cf090694435e"),
  },
  { match: /camp|drive|distribut/i, url: img("1469571486292-0ba58a3f068b") },
  {
    match: /general|trust|corpus|fund/i,
    url: img("1532629345422-7515f3d16bb6"),
  },
];

function hashIdx(seed: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return h % mod;
}

function pickImage(tile: Tile, idx = 0): string {
  if (tile.image) {
    return tile.image;
  }
  const hay = `${tile.title} ${tile.description ?? ""}`;
  const kw = KEYWORD_IMAGES.find(k => k.match.test(hay));
  if (kw) {
    return kw.url;
  }
  const pool = FALLBACK_IMAGES[tile.kind] ?? FALLBACK_IMAGES.campaign;
  return pool[hashIdx((tile.id || tile.title) + idx, pool.length)];
}

type FilterKey = "all" | "inkind" | string;

type CategoryFilter = { id: string; name: string };

export default function DonateHub() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [campaigns, setCampaigns] = useState<Tile[]>([]);
  const [categories, setCategories] = useState<CategoryFilter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>("all");

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [schemesResult, categoriesResult] = await Promise.allSettled([
          publicDonationSchemeService.listActiveSchemes({
            page: 1,
            page_size: 100,
          }),
          publicDonationCategoryService.listActiveCategories({
            page: 1,
            page_size: 100,
          }),
        ]);

        if (ignore) {
          return;
        }

        if (schemesResult.status === "rejected") {
          throw schemesResult.reason;
        }

        const tiles = schemesResult.value.data.map(schemeToHubTile);
        setCampaigns(tiles);

        const categoryList =
          categoriesResult.status === "fulfilled"
            ? categoriesResult.value.data.map(item => ({
              id: String(item.id),
              name: item.name,
            }))
            : [];

        if (categoryList.length === 0 && tiles.length > 0) {
          const seen = new Map<string, string>();
          for (const tile of tiles) {
            if (tile.categoryId && !seen.has(tile.categoryId)) {
              seen.set(tile.categoryId, tile.categoryName);
            }
          }
          setCategories(
            [...seen.entries()].map(([id, name]) => ({ id, name }))
          );
        } else {
          setCategories(categoryList);
        }
      } catch {
        if (!ignore) {
          setError(t("pages.donateHub.causes.unableToLoad"));
          setCampaigns([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      ignore = true;
    };
  }, [t]);

  const totals = useMemo(() => getHubAggregateStats(campaigns), [campaigns]);

  const filteredCampaigns = useMemo(() => {
    if (filter === "all") {
      return campaigns;
    }
    if (filter === "inkind") {
      return [];
    }
    return campaigns.filter(campaign => campaign.categoryId === filter);
  }, [campaigns, filter]);

  const featuredCampaigns = useMemo(
    () =>
      filteredCampaigns.filter(
        c =>
          (c.target ?? 0) > 0 &&
          (c.dateStatus === "active" ||
            c.dateStatus === "live" ||
            c.dateStatus === "upcoming" ||
            !c.dateStatus)
      ),
    [filteredCampaigns]
  );

  const campaignsByCategory = useMemo(() => {
    const source =
      filter === "all"
        ? campaigns
        : filter === "inkind"
          ? []
          : campaigns.filter(c => c.categoryId === filter);

    const categoryOrder =
      filter === "all"
        ? categories
        : categories.filter(category => category.id === filter);

    const groups: { category: CategoryFilter; items: Tile[] }[] = [];

    for (const category of categoryOrder) {
      const items = source.filter(c => c.categoryId === category.id);
      if (items.length > 0) {
        groups.push({ category, items });
      }
    }

    const knownIds = new Set(categoryOrder.map(category => category.id));
    const uncategorized = source.filter(c => !knownIds.has(c.categoryId));
    if (uncategorized.length > 0) {
      groups.push({
        category: { id: "other", name: "Other" },
        items: uncategorized,
      });
    }

    return groups;
  }, [campaigns, categories, filter]);

  const filterChips = useMemo(
    () => [
      { key: "all" as const, label: t("pages.donateHub.filter.all"), icon: Filter },
      ...categories.map(category => ({
        key: category.id,
        label: category.name,
        icon: Target,
      })),
      { key: "inkind" as const, label: t("pages.donateHub.filter.inKind"), icon: Package },
    ],
    [categories, t]
  );

  const go = (t: Tile) => {
    nav({ to: "/donate/c/$kind/$id", params: { kind: t.kind, id: t.id } });
  };

  const showCampaignSections = filter !== "inkind";
  const showInKind = filter === "all" || filter === "inkind";
  const showOngoingSection =
    showCampaignSections && filter === "all" && featuredCampaigns.length > 0;

  return (
    <PageShell>
      {/* HERO */}
      <section className="relative isolate overflow-hidden text-primary-foreground">
        {/* Rich multi-stop gradient using logo colors (indigo disc → saffron → leaf) */}
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(120% 80% at 0% 0%, oklch(0.42 0.14 250) 0%, transparent 55%)," +
              "radial-gradient(100% 80% at 100% 100%, oklch(0.55 0.18 55 / 0.85) 0%, transparent 55%)," +
              "radial-gradient(90% 70% at 50% 120%, oklch(0.48 0.14 145 / 0.55) 0%, transparent 60%)," +
              "linear-gradient(135deg, oklch(0.28 0.10 250) 0%, oklch(0.32 0.13 255) 50%, oklch(0.36 0.14 260) 100%)",
          }}
        />
        <img
          src="https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?auto=format&fit=crop&w=1800&q=70"
          alt=""
          aria-hidden
          className="absolute inset-0 -z-10 h-full w-full object-cover opacity-20 mix-blend-luminosity"
        />
        <div
          className="absolute inset-0 -z-10 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 20%, white 1px, transparent 1px), radial-gradient(circle at 75% 60%, white 1px, transparent 1px)",
            backgroundSize: "60px 60px, 90px 90px",
          }}
        />
        <div className="relative mx-auto max-w-6xl px-4 py-14 md:py-20">
          <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div>
              <span
                className="bg-gold/20 anim-fade-up inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gold"
                style={{ animationDelay: "100ms" }}
              >
                <Flame className="h-3.5 w-3.5" /> {t("pages.donateHub.hero.liveDonations")}
              </span>
              <h1 className="mt-4 font-serif text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
                <span className="anim-line" style={{ animationDelay: "220ms" }}>
                  <span>{t("pages.donateHub.hero.titleLine1")}</span>
                </span>
                <span className="anim-line" style={{ animationDelay: "360ms" }}>
                  <span>{t("pages.donateHub.hero.titleLine2")}</span>
                </span>
              </h1>
              <p
                className="text-primary-foreground/80 anim-fade-up mt-4 max-w-xl text-base md:text-lg"
                style={{ animationDelay: "500ms" }}
              >
                {t("pages.donateHub.hero.subtitle")}
              </p>
              <div
                className="anim-fade-up mt-6 flex flex-wrap gap-3"
                style={{ animationDelay: "640ms" }}
              >
                <Button
                  size="lg"
                  className="hover:bg-gold/90 btn-lift gap-2 bg-gold text-gold-foreground"
                  asChild
                >
                  <a href="#causes">
                    <Heart className="h-4 w-4" /> {t("pages.donateHub.hero.browseCauses")}
                  </a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-primary-foreground/30 hover:bg-primary-foreground/10 bg-transparent text-primary-foreground"
                  asChild
                >
                  <Link to="/donate/items">
                    <Package className="mr-1 h-4 w-4" /> {t("pages.donateHub.hero.donateInKind")}
                  </Link>
                </Button>
              </div>
            </div>
            <div
              className="border-primary-foreground/20 bg-primary-foreground/10 anim-zoom-in rounded-3xl border p-6 backdrop-blur"
              style={{ animationDelay: "480ms" }}
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gold">
                <TrendingUp className="h-4 w-4" /> {t("pages.donateHub.hero.totalRaised")}
              </div>
              <div className="mt-3 font-serif text-4xl font-bold md:text-5xl">
                {inr(totals.raised)}
              </div>
              <div className="text-primary-foreground/70 mt-1 text-sm">
                {t("pages.donateHub.hero.ofGoal").replace("{goal}", inr(totals.goal))}
              </div>
              <div className="mt-4">
                <div className="bg-primary-foreground/15 h-2.5 w-full overflow-hidden rounded-full">
                  <div
                    className="to-gold/70 h-full rounded-full bg-gradient-to-r from-gold transition-all"
                    style={{ width: `${totals.pct}%` }}
                  />
                </div>
                <div className="text-primary-foreground/70 mt-2 flex justify-between text-xs">
                  <span>{t("pages.donateHub.hero.pctReached").replace("{pct}", totals.pct.toFixed(0))}</span>
                  <span>{t("pages.donateHub.hero.activeDrivesCount").replace("{count}", String(totals.drives))}</span>
                </div>
              </div>
              <div className="border-primary-foreground/20 mt-5 grid grid-cols-2 gap-3 border-t pt-4">
                <Stat
                  icon={Target}
                  label={t("pages.donateHub.hero.activeDrivesLabel")}
                  value={String(totals.drives)}
                />
                <Stat icon={Award} label={t("pages.donateHub.hero.taxEligibleLabel")} value="100%" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div
        id="causes"
        className="mx-auto max-w-6xl scroll-mt-20 space-y-12 px-4 py-10"
      >
        {/* Filter chips */}
        <Reveal variant="fade" className="-mt-2 flex flex-wrap gap-2">
          {filterChips.map(f => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition ${active ? "border-gold bg-gold text-gold-foreground" : "border-border bg-card text-foreground hover:border-gold"}`}
              >
                <f.icon className="h-3.5 w-3.5" /> {f.label}
              </button>
            );
          })}
        </Reveal>

        {error && (
          <p className="text-center text-sm text-destructive">{error}</p>
        )}

        {showOngoingSection && (
          <section key={`ongoing-${filter}`} className="anim-fade-up">
            <SectionHeading
              icon={Target}
              title={t("pages.donateHub.causes.ongoingTitle")}
              subtitle={t("pages.donateHub.causes.ongoingSubtitle")}
            />
            <Stagger className="grid gap-4 md:grid-cols-3">
              {featuredCampaigns.map((c, i) => (
                <StaggerItem key={c.id} index={i} className="grid">
                  <CampaignCard c={c} onPick={go} />
                </StaggerItem>
              ))}
            </Stagger>
          </section>
        )}

        {showCampaignSections &&
          campaignsByCategory.map(({ category, items }) => {
            const ongoingIds = new Set(featuredCampaigns.map(c => c.id));
            const sectionItems =
              showOngoingSection && filter === "all"
                ? items.filter(c => !ongoingIds.has(c.id))
                : items;
            if (sectionItems.length === 0) {
              return null;
            }

            return (
              <section key={`${filter}-${category.id}`} className="anim-fade-up">
                <SectionHeading
                  icon={Target}
                  title={category.name}
                  subtitle={
                    filter === "all"
                      ? t("pages.donateHub.causes.browseCategorySubtitle")
                      : t("pages.donateHub.causes.allCategorySubtitle").replace(
                        "{category}",
                        category.name
                      )
                  }
                />
                <Stagger className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {sectionItems.map((c, i) => (
                    <StaggerItem key={c.id} index={i} className="grid">
                      <CampaignCard c={c} onPick={go} />
                    </StaggerItem>
                  ))}
                </Stagger>
              </section>
            );
          })}

        {!loading && showCampaignSections && filteredCampaigns.length === 0 && (
          <p className="py-10 text-center text-muted-foreground">
            {t("pages.donateHub.causes.noCampaignsFound")}
          </p>
        )}

        {showInKind && (
          <Reveal
            as="section"
            variant="wipe-left"
            className="border-gold/30 from-gold/10 rounded-3xl border bg-gradient-to-br to-transparent p-6 md:p-10"
          >
            <div className="grid gap-5 md:grid-cols-[1fr_auto] md:items-center">
              <div className="flex items-start gap-4">
                <div className="bg-gold/20 grid h-12 w-12 shrink-0 place-items-center rounded-full text-gold">
                  <Package className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="font-serif text-xl font-bold text-primary md:text-2xl">
                    {t("pages.donateHub.inKindBanner.title")}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground md:text-base">
                    {t("pages.donateHub.inKindBanner.subtitle")}
                  </p>
                </div>
              </div>
              <Button
                asChild
                size="lg"
                className="hover:bg-gold/90 btn-lift bg-gold text-gold-foreground md:shrink-0"
              >
                <Link to="/donate/items">
                  {t("pages.donateHub.inKindBanner.cta")} <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </Reveal>
        )}

        {loading && (
          <p className="text-center text-muted-foreground">{t("pages.donateHub.causes.loadingCauses")}</p>
        )}

        {/* Trust strip */}
        <section className="border-gold/30 from-gold/5 rounded-3xl border bg-gradient-to-br to-transparent p-6 md:p-8">
          <Stagger className="grid gap-6 md:grid-cols-3">
            <StaggerItem index={0}>
              <Trust
                icon={Shield}
                title={t("pages.donateHub.trust.transparentTitle")}
                body={t("pages.donateHub.trust.transparentBody")}
              />
            </StaggerItem>
            <StaggerItem index={1}>
              <Trust
                icon={Award}
                title={t("pages.donateHub.trust.taxTitle")}
                body={t("pages.donateHub.trust.taxBody")}
              />
            </StaggerItem>
            <StaggerItem index={2}>
              <Trust
                icon={Heart}
                title={t("pages.donateHub.trust.communityTitle")}
                body={t("pages.donateHub.trust.communityBody")}
              />
            </StaggerItem>
          </Stagger>
        </section>
      </div>

      {/* Bank & UPI Details */}
      <ManavSevaBankDetails />
    </PageShell>
  );
}

function SectionHeading({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: any;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-gold" />
        <h2 className="font-serif text-xl font-bold text-primary md:text-2xl">
          {title}
        </h2>
      </div>
      {subtitle && (
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      )}
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value: string;
}) {
  return (
    <div>
      <div className="text-primary-foreground/70 flex items-center gap-1.5 text-xs">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-0.5 font-serif text-xl font-bold">{value}</div>
    </div>
  );
}

function Trust({
  icon: Icon,
  title,
  body,
}: {
  icon: any;
  title: string;
  body: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="bg-gold/15 grid h-10 w-10 shrink-0 place-items-center rounded-full text-gold">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h3 className="!font-sans !text-2xl !font-semibold !text-primary">{title}</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

function CampaignCard({ c, onPick }: { c: Tile; onPick: (t: Tile) => void }) {
  const { t } = useI18n();
  const pct = c.target
    ? Math.min(100, ((c.collected ?? 0) / c.target) * 100)
    : 0;
  const remaining = Math.max(0, (c.target ?? 0) - (c.collected ?? 0));
  return (
    <button
      type="button"
      onClick={() => onPick(c)}
      className="group relative overflow-hidden rounded-2xl border border-border bg-card text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
    >
      <div className="relative h-44 w-full overflow-hidden bg-muted">
        <img
          src={pickImage(c)}
          alt={c.title}
          loading="lazy"
          className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/10" />
        {c.badge && (
          <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-gold px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-foreground shadow">
            <Flame className="h-3 w-3" /> {c.badge}
          </div>
        )}
        <h3 className="absolute inset-x-3 bottom-3 font-serif text-lg font-bold !text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.85)]">
          {c.title}
        </h3>
      </div>
      <div className="p-4">
        {(c.target ?? 0) > 0 ? (
          <>
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="font-bold text-primary">
                {inr(c.collected ?? 0)}
              </span>
              <span className="text-xs text-muted-foreground">
                {t("pages.donateHub.card.ofTarget").replace("{target}", inr(c.target ?? 0))}
              </span>
            </div>
            <Progress value={pct} className="mt-2 h-2" />
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="font-semibold text-gold">
                {t("pages.donateHub.card.fundedPct").replace("{pct}", pct.toFixed(0))}
              </span>
              <span className="text-muted-foreground">
                {t("pages.donateHub.card.stillNeeded").replace("{amount}", inr(remaining))}
              </span>
            </div>
          </>
        ) : (
          c.description && (
            <p className="line-clamp-2 text-sm text-muted-foreground">
              {c.description}
            </p>
          )
        )}
        <div className="group-hover:bg-primary/90 mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition">
          {t("pages.donateHub.card.donateNow")} <ArrowRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </button>
  );
}
